import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import MachineSearchBox from '../components/MachineSearchBox';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS_GR = {
  1: "ΙΑΝΟΥΑΡΙΟΣ", 2: "ΦΕΒΡΟΥΑΡΙΟΣ", 3: "ΜΑΡΤΙΟΣ", 4: "ΑΠΡΙΛΙΟΣ",
  5: "ΜΑΙΟΣ", 6: "ΙΟΥΝΙΟΣ", 7: "ΙΟΥΛΙΟΣ", 8: "ΑΥΓΟΥΣΤΟΣ",
  9: "ΣΕΠΤΕΜΒΡΙΟΣ", 10: "ΟΚΤΩΒΡΙΟΣ", 11: "ΝΟΕΜΒΡΙΟΣ", 12: "ΔΕΚΕΜΒΡΙΟΣ"
};

// Bulk Import Modal Component
const BulkImportModal = ({ isOpen, onClose, onSuccess, importType }) => {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [readingDate, setReadingDate] = useState('2026-' + String(new Date().getMonth() + 1).padStart(2, '0') + '-01');
  const [csvContent, setCsvContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Update readingDate when year or month changes
  const handleYearChange = (newYear) => {
    setYear(newYear);
    setReadingDate(`${newYear}-${String(month).padStart(2, '0')}-01`);
  };

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
    setReadingDate(`${year}-${String(newMonth).padStart(2, '0')}-01`);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    
    if (ext === 'xlsx' || ext === 'xls') {
      // Excel file - convert to CSV
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const csvText = XLSX.utils.sheet_to_csv(sheet, { FS: ',' });
      setCsvContent(csvText);
      toast.success(`Excel αρχείο "${file.name}" φορτώθηκε`);
    } else {
      // CSV/TXT file
      const text = await file.text();
      setCsvContent(text);
    }
  };

  const handleImport = async () => {
    if (!csvContent.trim()) {
      toast.error('Παρακαλώ επιλέξτε αρχείο CSV ή Excel');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/readings/bulk-import`, {
        csv_data: csvContent,
        year: year,
        month: month,
        reading_date: readingDate,
        import_type: importType === 'counters' ? 'counters' : 'pages'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResult(response.data);
      toast.success(`Επιτυχής εισαγωγή: ${response.data.successful} μηχανήματα`);
      
      if (response.data.successful > 0) {
        onSuccess();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα κατά την εισαγωγή');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setCsvContent('');
    setResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-blue-600" />
            {importType === 'counters' ? 'ΜΑΖΙΚΗ ΕΙΣΑΓΩΓΗ ΜΕΤΡΗΤΩΝ' : 'ΜΑΖΙΚΗ ΕΙΣΑΓΩΓΗ ΜΕΤΡΗΣΕΩΝ'}
          </h2>

          {!result ? (
            <>
              {/* Settings */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <Label>Έτος</Label>
                  <Input
                    type="number"
                    value={year}
                    onChange={(e) => handleYearChange(parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Μήνας</Label>
                  <select
                    value={month}
                    onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {Object.entries(MONTHS_GR).map(([num, name]) => (
                      <option key={num} value={num}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Ημερομηνία Μέτρησης</Label>
                  <Input
                    type="date"
                    value={readingDate}
                    onChange={(e) => setReadingDate(e.target.value)}
                  />
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-6">
                <Label>Αρχείο CSV ή Excel</Label>
                <p className="text-sm text-gray-500 mb-2">
                  Μορφή: <code className="bg-gray-100 px-1">ΣΕΙΡΙΑΚΟΣ,BLACK_A4,BLACK_A3,COLOR_A4,COLOR_A3</code>
                </p>
                <p className="text-xs text-gray-400 mb-2">
                  Υποστηρίζονται: .csv, .txt, .xlsx, .xls (κάθε στήλη και ένας αριθμός)
                </p>
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border rounded-md"
                  data-testid="bulk-file-input"
                />
              </div>

              {/* Preview */}
              {csvContent && (
                <div className="mb-6">
                  <Label>Προεπισκόπηση ({csvContent.split('\n').filter(l => l.trim()).length} γραμμές)</Label>
                  <pre className="bg-gray-100 p-3 rounded text-xs max-h-40 overflow-y-auto">
                    {csvContent.split('\n').slice(0, 10).join('\n')}
                    {csvContent.split('\n').length > 10 && '\n...'}
                  </pre>
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={resetAndClose}>Ακύρωση</Button>
                <Button onClick={handleImport} disabled={loading || !csvContent}>
                  {loading ? 'Εισαγωγή...' : 'Εισαγωγή'}
                </Button>
              </div>
            </>
          ) : (
            /* Results Report */
            <div>
              <div id="bulk-import-report">
                <div className="text-center mb-4 hidden print:block">
                  <h1 className="text-xl font-bold">ΑΝΑΦΟΡΑ ΜΑΖΙΚΗΣ ΕΙΣΑΓΩΓΗΣ</h1>
                  <p className="text-sm text-gray-500">{importType === 'counters' ? 'ΜΕΤΡΗΤΕΣ' : 'ΜΕΤΡΗΣΕΙΣ'} - {month}/{year}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-600">{result.total_rows}</div>
                    <div className="text-sm text-gray-600">Συνολικές Γραμμές</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-green-600">{result.successful}</div>
                    <div className="text-sm text-gray-600">Επιτυχείς</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-orange-600">{result.not_found}</div>
                    <div className="text-sm text-gray-600">Δεν Βρέθηκαν</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-red-600">
                      {result.already_exists + result.invalid_value + result.future_month_exists}
                    </div>
                    <div className="text-sm text-gray-600">Αποτυχίες</div>
                  </div>
                </div>

                {/* Detailed Results */}
                <div className="max-h-60 overflow-y-auto border rounded print:max-h-none print:overflow-visible">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0 print:bg-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left">S/N</th>
                        <th className="px-3 py-2 text-left">Μηχάνημα</th>
                        <th className="px-3 py-2 text-left">Κατάσταση</th>
                        <th className="px-3 py-2 text-left">Λεπτομέρειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.details.map((d, i) => (
                        <tr key={i} className={
                          d.status === 'OK' ? 'bg-green-50' :
                          d.status === 'NOT_FOUND' ? 'bg-red-50' :
                          'bg-yellow-50'
                        }>
                          <td className="px-3 py-1 font-mono text-xs">{d.serial}</td>
                          <td className="px-3 py-1 text-xs">{d.machine || '-'}</td>
                          <td className="px-3 py-1 text-xs">
                            {d.status === 'OK' && <CheckCircle className="h-3 w-3 text-green-600 inline mr-1" />}
                            {d.status === 'NOT_FOUND' && <XCircle className="h-3 w-3 text-red-600 inline mr-1" />}
                            {!['OK', 'NOT_FOUND'].includes(d.status) && <AlertCircle className="h-3 w-3 text-orange-600 inline mr-1" />}
                            {d.status}
                          </td>
                          <td className="px-3 py-1 text-xs">{d.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {result.errors.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 rounded">
                    <p className="font-medium text-red-700 mb-2">Σφάλματα:</p>
                    <ul className="text-sm text-red-600 list-disc pl-5">
                      {result.errors.map((err, i) => <li key={i}>{err}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6 print:hidden">
                <Button variant="outline" onClick={() => window.print()} data-testid="export-pdf-btn">
                  Εξαγωγή PDF
                </Button>
                <Button onClick={resetAndClose}>Κλείσιμο</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReadingsPageNew = () => {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('---');
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('---');
  const [machineData, setMachineData] = useState(null);
  const [allMachines, setAllMachines] = useState([]);
  
  // Bulk import modals
  const [showBulkCounters, setShowBulkCounters] = useState(false);
  const [showBulkPages, setShowBulkPages] = useState(false);
  
  // Year stats
  const [yearReadings, setYearReadings] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  
  // Form
  const [readingYear, setReadingYear] = useState(new Date().getFullYear());
  const [readingMonth, setReadingMonth] = useState(new Date().getMonth() + 1);
  const [readingDate, setReadingDate] = useState(new Date().toISOString().split('T')[0]); // NEW: Actual date of reading
  const [inputMode, setInputMode] = useState('ΜΕΤΡΗΤΗΣ');
  const [source, setSource] = useState('ISS LEX');
  
  // Counters (ΜΕΤΡΗΤΗΣ mode)
  const [counterBlack, setCounterBlack] = useState(0);
  const [counterColor, setCounterColor] = useState(0);
  const [counterA3, setCounterA3] = useState(0);
  const [counterA3Color, setCounterA3Color] = useState(0);
  
  // Pages (ΣΕΛΙΔΕΣ ΜΗΝΑ mode)
  const [pagesBlack, setPagesBlack] = useState(0);
  const [pagesColor, setPagesColor] = useState(0);
  const [pagesA3, setPagesA3] = useState(0);
  const [pagesA3Color, setPagesA3Color] = useState(0);
  
  // Previous/Next readings
  const [prevReading, setPrevReading] = useState(null);
  const [nextReading, setNextReading] = useState(null);
  const [existsReading, setExistsReading] = useState(false);
  
  // Preview
  const [previewReport, setPreviewReport] = useState(null);
  
  const [loading, setLoading] = useState(false);

  // Bulk entry state
  const [viewMode, setViewMode] = useState('single'); // 'single' or 'bulk'
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [bulkMonth, setBulkMonth] = useState(new Date().getMonth() + 1);
  const [bulkMachines, setBulkMachines] = useState([]);
  const [bulkValues, setBulkValues] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkSort, setBulkSort] = useState({ key: 'customer', dir: 'asc' });

  const MONTHS_NAMES = ['Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος','Μάιος','Ιούνιος',
    'Ιούλιος','Αύγουστος','Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος'];

  const fetchBulkData = async () => {
    setBulkLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/readings/bulk-entry/${bulkYear}/${bulkMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulkMachines(res.data);
      const initial = {};
      res.data.forEach(m => {
        initial[m.machine_id] = {
          a4_black: m.existing?.counter_value_black || '',
          a4_color: m.existing?.counter_value_color || '',
          a3_black: m.existing?.counter_value_a3 || '',
          a3_color: m.existing?.counter_value_a3_color || '',
        };
      });
      setBulkValues(initial);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης');
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'bulk') fetchBulkData();
  }, [viewMode, bulkYear, bulkMonth]);

  const handleBulkChange = (machineId, field, val) => {
    setBulkValues(prev => ({
      ...prev,
      [machineId]: { ...prev[machineId], [field]: val }
    }));
  };

  const handleBulkSave = async () => {
    const readings = [];
    for (const m of bulkMachines) {
      const v = bulkValues[m.machine_id];
      if (!v) continue;
      const a4b = parseInt(v.a4_black) || 0;
      const a4c = parseInt(v.a4_color) || 0;
      const a3b = parseInt(v.a3_black) || 0;
      const a3c = parseInt(v.a3_color) || 0;
      if (a4b === 0 && a4c === 0 && a3b === 0 && a3c === 0) continue;
      const ex = m.existing;
      if (ex && a4b === (ex.counter_value_black||0) && a4c === (ex.counter_value_color||0)
          && a3b === (ex.counter_value_a3||0) && a3c === (ex.counter_value_a3_color||0)) continue;
      readings.push({ machine_id: m.machine_id, counter_value_black: a4b, counter_value_color: a4c, counter_value_a3: a3b, counter_value_a3_color: a3c });
    }
    if (readings.length === 0) { toast.error('Δεν υπάρχουν αλλαγές'); return; }
    setBulkSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/readings/bulk`, { year: bulkYear, month: bulkMonth, readings }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchBulkData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Σφάλμα'); }
    finally { setBulkSaving(false); }
  };

  const handleBulkSort = (key) => {
    setBulkSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sortedBulkMachines = [...bulkMachines].sort((a, b) => {
    const dir = bulkSort.dir === 'asc' ? 1 : -1;
    const valA = (a[bulkSort.key] || '').toString().toLowerCase();
    const valB = (b[bulkSort.key] || '').toString().toLowerCase();
    return valA.localeCompare(valB, 'el') * dir;
  });

  const BulkSortHeader = ({ field, label }) => (
    <th className="px-2 py-2 text-left whitespace-nowrap cursor-pointer hover:bg-blue-800 select-none transition-colors"
      rowSpan={2}
      onClick={() => handleBulkSort(field)}>
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="text-[10px]">{bulkSort.key === field ? (bulkSort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
      </div>
    </th>
  );

  const bulkPendingCount = bulkMachines.filter(m => {
    const v = bulkValues[m.machine_id]; if (!v) return false;
    const a4b = parseInt(v.a4_black)||0, a4c = parseInt(v.a4_color)||0, a3b = parseInt(v.a3_black)||0, a3c = parseInt(v.a3_color)||0;
    if (a4b===0 && a4c===0 && a3b===0 && a3c===0) return false;
    const ex = m.existing;
    if (ex && a4b===(ex.counter_value_black||0) && a4c===(ex.counter_value_color||0) && a3b===(ex.counter_value_a3||0) && a3c===(ex.counter_value_a3_color||0)) return false;
    return true;
  }).length;

  useEffect(() => {
    fetchCustomers();
    fetchAvailableYears();
    fetchAllMachines();
  }, []);

  const fetchAllMachines = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/machines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllMachines(response.data);
    } catch (error) {
      console.error('Error fetching all machines:', error);
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/readings/available-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = response.data.years || [];
      setAvailableYears(years);
    } catch (error) {
      console.error('Error fetching available years:', error);
      // Fallback
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear]);
    }
  };

  useEffect(() => {
    if (selectedCustomer !== '---') {
      fetchMachines();
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (machineData) {
      fetchYearReadings();
      checkReadingContext();
    }
  }, [machineData, readingYear, readingMonth]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchMachines = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get customer ID
      const customer = customers.find(c => c.company_name === selectedCustomer);
      if (!customer) return;
      
      const response = await axios.get(`${API_URL}/machines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter by customer_id
      const customerMachines = response.data.filter(m => 
        m.customer_id === customer.id
      );
      
      setMachines(customerMachines);
    } catch (error) {
      console.error('Error fetching machines:', error);
    }
  };

  const fetchYearReadings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/readings/machine/${machineData.id}/year/${readingYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setYearReadings(response.data);
    } catch (error) {
      console.error('Error fetching year readings:', error);
    }
  };

  const checkReadingContext = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/readings/context/${machineData.id}/${readingYear}/${readingMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPrevReading(response.data.previous);
      setNextReading(response.data.next);
      setExistsReading(response.data.exists);
      
      // Set initial counter values to previous values
      if (response.data.previous) {
        setCounterBlack(response.data.previous.counter_value_black || 0);
        setCounterColor(response.data.previous.counter_value_color || 0);
        setCounterA3(response.data.previous.counter_value_a3 || 0);
        setCounterA3Color(response.data.previous.counter_value_a3_color || 0);
      } else {
        setCounterBlack(machineData.start_counter_black || 0);
        setCounterColor(machineData.start_counter_color || 0);
        setCounterA3(machineData.start_counter_a3 || 0);
        setCounterA3Color(machineData.start_counter_a3_color || 0);
      }
      
      // Reset pages
      setPagesBlack(0);
      setPagesColor(0);
      setPagesA3(0);
      setPagesA3Color(0);
      
    } catch (error) {
      console.error('Error checking reading context:', error);
    }
  };

  const handleMachineSelect = (machineSerial) => {
    setSelectedMachine(machineSerial);
    const machine = machines.find(m => m.machine_serial === machineSerial) 
                 || allMachines.find(m => m.machine_serial === machineSerial);
    setMachineData(machine);
    setPreviewReport(null);
  };

  const handleCheckAndReport = async () => {
    if (!machineData) return;

    // Calculate final counters based on input mode
    let finalBlack, finalColor, finalA3, finalA3Color;
    
    if (inputMode === 'ΜΕΤΡΗΤΗΣ') {
      finalBlack = counterBlack;
      finalColor = counterColor;
      finalA3 = counterA3;
      finalA3Color = counterA3Color;
    } else {
      const prevBlack = prevReading?.counter_value_black || machineData.start_counter_black || 0;
      const prevColor = prevReading?.counter_value_color || machineData.start_counter_color || 0;
      const prevA3 = prevReading?.counter_value_a3 || machineData.start_counter_a3 || 0;
      const prevA3Color = prevReading?.counter_value_a3_color || machineData.start_counter_a3_color || 0;
      
      finalBlack = prevBlack + pagesBlack;
      finalColor = prevColor + pagesColor;
      finalA3 = prevA3 + pagesA3;
      finalA3Color = prevA3Color + pagesA3Color;
    }

    // Validations
    if (existsReading) {
      toast.error(`❌ ΥΠΑΡΧΕΙ ΗΔΗ ΚΑΤΑΧΩΡΗΣΗ ΓΙΑ ${MONTHS_GR[readingMonth]} ${readingYear}!`);
      return;
    }

    // Check contract start date - compare YEAR and MONTH only, not the day
    if (machineData.contract_start) {
      try {
        const contractDate = new Date(machineData.contract_start);
        const contractYearMonth = contractDate.getFullYear() * 12 + contractDate.getMonth();
        const readingYearMonth = readingYear * 12 + (readingMonth - 1);
        
        if (readingYearMonth < contractYearMonth) {
          toast.error(`❌ ΔΕΝ ΕΠΙΤΡΕΠΕΤΑΙ ΚΑΤΑΧΩΡΗΣΗ ΠΡΙΝ ΤΗΝ ΕΝΑΡΞΗ ΣΥΜΒΟΛΑΙΟΥ (${machineData.contract_start})!`);
          return;
        }
      } catch (error) {
        console.error('Contract date validation error:', error);
      }
    }

    // Check bounds with next reading
    if (nextReading) {
      if (finalBlack > nextReading.counter_value_black) {
        toast.error(`❌ Ο ΜΕΤΡΗΤΗΣ A4 BLACK (${finalBlack}) ΞΕΠΕΡΝΑ ΤΗΝ ΕΠΟΜΕΝΗ ΕΝΔΕΙΞΗ (${nextReading.counter_value_black})!`);
        return;
      }
      if (machineData.machine_color === 'Color' && finalColor > nextReading.counter_value_color) {
        toast.error(`❌ Ο ΜΕΤΡΗΤΗΣ A4 COLOR (${finalColor}) ΞΕΠΕΡΝΑ ΤΗΝ ΕΠΟΜΕΝΗ ΕΝΔΕΙΞΗ (${nextReading.counter_value_color})!`);
        return;
      }
    }

    // Check that counter is not LESS than previous reading or start counter
    const refBlack = prevReading?.counter_value_black ?? machineData.start_counter_black ?? 0;
    const refColor = prevReading?.counter_value_color ?? machineData.start_counter_color ?? 0;
    const refA3 = prevReading?.counter_value_a3 ?? machineData.start_counter_a3 ?? 0;
    const refA3Color = prevReading?.counter_value_a3_color ?? machineData.start_counter_a3_color ?? 0;

    if (inputMode === 'ΜΕΤΡΗΤΗΣ') {
      if (finalBlack < refBlack) {
        toast.error(`❌ Ο ΜΕΤΡΗΤΗΣ A4 BLACK (${finalBlack.toLocaleString()}) ΕΙΝΑΙ ΜΙΚΡΟΤΕΡΟΣ ΑΠΟ ${prevReading ? 'ΤΗΝ ΠΡΟΗΓΟΥΜΕΝΗ ΕΝΔΕΙΞΗ' : 'ΤΟΝ ΑΡΧΙΚΟ ΜΕΤΡΗΤΗ'} (${refBlack.toLocaleString()})!`);
        return;
      }
      if (machineData.machine_color === 'Color' && finalColor < refColor) {
        toast.error(`❌ Ο ΜΕΤΡΗΤΗΣ A4 COLOR (${finalColor.toLocaleString()}) ΕΙΝΑΙ ΜΙΚΡΟΤΕΡΟΣ ΑΠΟ ${prevReading ? 'ΤΗΝ ΠΡΟΗΓΟΥΜΕΝΗ ΕΝΔΕΙΞΗ' : 'ΤΟΝ ΑΡΧΙΚΟ ΜΕΤΡΗΤΗ'} (${refColor.toLocaleString()})!`);
        return;
      }
      if (isA3 && finalA3 < refA3) {
        toast.error(`❌ Ο ΜΕΤΡΗΤΗΣ A3 BLACK (${finalA3.toLocaleString()}) ΕΙΝΑΙ ΜΙΚΡΟΤΕΡΟΣ ΑΠΟ ${prevReading ? 'ΤΗΝ ΠΡΟΗΓΟΥΜΕΝΗ ΕΝΔΕΙΞΗ' : 'ΤΟΝ ΑΡΧΙΚΟ ΜΕΤΡΗΤΗ'} (${refA3.toLocaleString()})!`);
        return;
      }
      if (isA3Color && finalA3Color < refA3Color) {
        toast.error(`❌ Ο ΜΕΤΡΗΤΗΣ A3 COLOR (${finalA3Color.toLocaleString()}) ΕΙΝΑΙ ΜΙΚΡΟΤΕΡΟΣ ΑΠΟ ${prevReading ? 'ΤΗΝ ΠΡΟΗΓΟΥΜΕΝΗ ΕΝΔΕΙΞΗ' : 'ΤΟΝ ΑΡΧΙΚΟ ΜΕΤΡΗΤΗ'} (${refA3Color.toLocaleString()})!`);
        return;
      }
    }

    // Calculate pages and costs
    const prevBlack = prevReading?.counter_value_black || machineData.start_counter_black || 0;
    const prevColor = prevReading?.counter_value_color || machineData.start_counter_color || 0;
    const prevA3 = prevReading?.counter_value_a3 || machineData.start_counter_a3 || 0;
    const prevA3Color = prevReading?.counter_value_a3_color || machineData.start_counter_a3_color || 0;

    const calculatedPagesBlack = finalBlack - prevBlack;
    const calculatedPagesColor = finalColor - prevColor;
    const calculatedPagesA3 = finalA3 - prevA3;
    const calculatedPagesA3Color = finalA3Color - prevA3Color;

    const priceBlack = machineData.price_per_page_black || 0;
    const priceColor = machineData.price_per_page_color || 0;
    const priceA3 = machineData.price_per_page_a3 || 0;
    const priceA3Color = machineData.price_per_page_a3_color || 0;
    const priceMonthly = machineData.price_per_month || 0;

    const costBlack = calculatedPagesBlack * priceBlack;
    const costColor = calculatedPagesColor * priceColor;
    const costA3 = calculatedPagesA3 * priceA3;
    const costA3Color = calculatedPagesA3Color * priceA3Color;
    const totalNet = priceMonthly + costBlack + costColor + costA3 + costA3Color;

    // Generate report text
    let report = `ΚΥΡΙΑΚΙΔΗΣ Ε.Ε. - REPORT\n${'='.repeat(50)}\n`;
    report += `ΠΕΛΑΤΗΣ: ${selectedCustomer}\nS/N: ${machineData.machine_serial}\n`;
    report += `ΜΗΝΑΣ: ${MONTHS_GR[readingMonth]} ${readingYear}\n${'-'.repeat(50)}\n\n`;
    
    // ========== ΜΕΤΡΗΣΕΙΣ ΠΡΩΤΑ ==========
    // Get previous date - prioritize reading_date, fallback to date_recorded
    let prevDateStr = null;
    if (prevReading) {
      // Check if reading_date exists and is not null/empty
      if (prevReading.reading_date && prevReading.reading_date.trim() !== '') {
        prevDateStr = prevReading.reading_date;
      } 
      // Fallback to date_recorded if reading_date doesn't exist
      else if (prevReading.date_recorded && prevReading.date_recorded.trim() !== '') {
        prevDateStr = prevReading.date_recorded.split(' ')[0]; // Extract date part only
      }
    }
    
    const currentDateStr = readingDate; // Use the selected reading date
    
    // Format dates properly
    const formatDate = (dateStr) => {
      if (!dateStr) return 'ΑΡΧΙΚΗ';
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return 'ΑΡΧΙΚΗ';
        return date.toLocaleDateString('el-GR');
      } catch {
        return 'ΑΡΧΙΚΗ';
      }
    };
    
    const prevDateFormatted = formatDate(prevDateStr);
    const currentDateFormatted = formatDate(currentDateStr);
    
    // A4 BLACK
    if (calculatedPagesBlack > 0 || finalBlack > 0) {
      report += `A4 BLACK:\n`;
      report += `  ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: ${prevDateFormatted} - ΣΕΛΙΔΕΣ: ${prevBlack}\n`;
      report += `  ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: ${currentDateFormatted} - ΣΕΛΙΔΕΣ: ${finalBlack}\n`;
      report += `  ΣΥΝΟΛΟ: ${finalBlack} - ${prevBlack} = ${calculatedPagesBlack}\n\n\n`;
    }
    
    // A4 COLOR
    if (machineData.machine_color === 'Color' && (calculatedPagesColor > 0 || finalColor > 0)) {
      report += `A4 COLOR:\n`;
      report += `  ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: ${prevDateFormatted} - ΣΕΛΙΔΕΣ: ${prevColor}\n`;
      report += `  ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: ${currentDateFormatted} - ΣΕΛΙΔΕΣ: ${finalColor}\n`;
      report += `  ΣΥΝΟΛΟ: ${finalColor} - ${prevColor} = ${calculatedPagesColor}\n\n\n`;
    }
    
    // A3 BLACK
    if (machineData.machine_type.includes('A3') && (calculatedPagesA3 > 0 || finalA3 > 0)) {
      report += `A3 BLACK:\n`;
      report += `  ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: ${prevDateFormatted} - ΣΕΛΙΔΕΣ: ${prevA3}\n`;
      report += `  ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: ${currentDateFormatted} - ΣΕΛΙΔΕΣ: ${finalA3}\n`;
      report += `  ΣΥΝΟΛΟ: ${finalA3} - ${prevA3} = ${calculatedPagesA3}\n\n\n`;
    }
    
    // A3 COLOR
    if (machineData.machine_type.includes('A3') && machineData.machine_color === 'Color' && (calculatedPagesA3Color > 0 || finalA3Color > 0)) {
      report += `A3 COLOR:\n`;
      report += `  ΠΡΟΗΓ. ΜΕΤΡΗΣΗ: ${prevDateFormatted} - ΣΕΛΙΔΕΣ: ${prevA3Color}\n`;
      report += `  ΤΕΛΕΥΤ. ΜΕΤΡΗΣΗ: ${currentDateFormatted} - ΣΕΛΙΔΕΣ: ${finalA3Color}\n`;
      report += `  ΣΥΝΟΛΟ: ${finalA3Color} - ${prevA3Color} = ${calculatedPagesA3Color}\n\n\n`;
    }
    
    // ========== ΟΙΚΟΝΟΜΙΚΑ ΜΕΤΑ ==========
    if (calculatedPagesBlack > 0) {
      report += `A4 BLACK: ${calculatedPagesBlack} x ${priceBlack.toFixed(4)}€ = ${costBlack.toFixed(2)}€\n`;
    }
    
    if (machineData.machine_color === 'Color' && calculatedPagesColor > 0) {
      report += `A4 COLOR: ${calculatedPagesColor} x ${priceColor.toFixed(4)}€ = ${costColor.toFixed(2)}€\n`;
    }
    
    if (machineData.machine_type.includes('A3') && calculatedPagesA3 > 0) {
      report += `A3 BLACK: ${calculatedPagesA3} x ${priceA3.toFixed(4)}€ = ${costA3.toFixed(2)}€\n`;
    }
    
    if (machineData.machine_type.includes('A3') && machineData.machine_color === 'Color' && calculatedPagesA3Color > 0) {
      report += `A3 COLOR: ${calculatedPagesA3Color} x ${priceA3Color.toFixed(4)}€ = ${costA3Color.toFixed(2)}€\n`;
    }
    
    report += `ΠΑΓΙΟ: ${priceMonthly.toFixed(2)}€\n`;
    report += `ΚΑΘΑΡΗ ΑΞΙΑ: ${totalNet.toFixed(2)}€\n`;
    report += `ΤΕΛΙΚΟ (ΜΕ ΦΠΑ 24%): ${(totalNet * 1.24).toFixed(2)}€\n`;
    report += `${'='.repeat(50)}\nΠΗΓΗ: ${source} | ΗΜ/ΝΙΑ: ${new Date().toLocaleDateString('el-GR')}\n`;
    report += `${'='.repeat(50)}`;

    setPreviewReport({
      machineId: machineData.id,
      year: readingYear,
      month: readingMonth,
      counterBlack: finalBlack,
      counterColor: finalColor,
      counterA3: finalA3,
      counterA3Color: finalA3Color,
      report,
      serialNumber: machineData.machine_serial
    });

    toast.success('✅ Report δημιουργήθηκε!');
  };

  const handleFinalSave = async () => {
    if (!previewReport) {
      toast.error('Δεν υπάρχει report για αποθήκευση');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/readings`,
        {
          machine_id: previewReport.machineId,
          reading_year: previewReport.year,
          reading_month: previewReport.month,
          counter_value_black: previewReport.counterBlack,
          counter_value_color: previewReport.counterColor,
          counter_value_a3: previewReport.counterA3,
          counter_value_a3_color: previewReport.counterA3Color,
          reading_date: readingDate,
          date_recorded: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('✅ ΕΠΙΤΥΧΗΣ ΚΑΤΑΧΩΡΗΣΗ!');

      // Get the reading ID from response
      const readingId = response.data.id;

      // Download TXT report from new endpoint
      try {
        const reportResponse = await fetch(
          `${API_URL}/readings/${readingId}/report-txt`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!reportResponse.ok) throw new Error('Report download failed');

        const contentDisposition = reportResponse.headers.get('content-disposition');
        let filename = `Report_${previewReport.serialNumber}_${previewReport.month}_${previewReport.year}.txt`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }

        const blob = await reportResponse.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('📄 TXT Report κατέβηκε!');
      } catch (reportError) {
        console.error('Report download error:', reportError);
        toast.error('⚠️ Η μέτρηση αποθηκεύτηκε αλλά το report δεν κατέβηκε');
      }

      // Reset
      setPreviewReport(null);
      fetchYearReadings();
      checkReadingContext();

    } catch (error) {
      console.error('Save error:', error);
      toast.error('❌ ΣΦΑΛΜΑ: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewReport(null);
  };

  const isA3 = machineData?.machine_type?.includes('A3');
  const isA3Color = isA3 && machineData?.machine_color === 'Color';

  return (
    <div className="space-y-6 p-6">
      {/* Header with Mode Toggle */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h1 className="text-xl sm:text-3xl font-bold">ΜΕΤΡΗΣΕΙΣ</h1>
        <div className="flex gap-2">
          <Button onClick={() => setViewMode('single')} variant={viewMode === 'single' ? 'default' : 'outline'} className={viewMode === 'single' ? 'bg-[#004a99]' : ''}>
            Μεμονωμένη Εισαγωγή
          </Button>
          <Button onClick={() => setViewMode('bulk')} variant={viewMode === 'bulk' ? 'default' : 'outline'} className={viewMode === 'bulk' ? 'bg-green-600 hover:bg-green-700' : ''}>
            Μαζική Εισαγωγή
          </Button>
          <Button onClick={() => setShowBulkCounters(true)} variant="outline" className="gap-2">
            <Upload className="h-4 w-4" /> CSV Import
          </Button>
        </div>
      </div>

      {/* Bulk Import Modal (CSV) */}
      <BulkImportModal isOpen={showBulkCounters} onClose={() => setShowBulkCounters(false)} onSuccess={fetchCustomers} importType="counters" />
      <BulkImportModal isOpen={showBulkPages} onClose={() => setShowBulkPages(false)} onSuccess={fetchCustomers} importType="pages" />

      {viewMode === 'bulk' ? (
        /* ========== BULK ENTRY VIEW ========== */
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <CardTitle>ΜΑΖΙΚΗ ΕΙΣΑΓΩΓΗ ΜΕΤΡΗΤΩΝ</CardTitle>
                <div className="flex gap-2 items-center">
                  <select value={bulkMonth} onChange={e => setBulkMonth(Number(e.target.value))} className="h-10 px-3 border rounded-md" data-testid="bulk-month">
                    {MONTHS_NAMES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
                  </select>
                  <Input type="number" value={bulkYear} onChange={e => setBulkYear(Number(e.target.value))} className="w-24" data-testid="bulk-year" />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">Βάλτε τον μετρητή κάθε μηχανήματος. Αν αλλάξετε υπάρχοντα μετρητή, οι σελίδες κρατιούνται σταθερές και οι υπόλοιποι μετρητές αναπροσαρμόζονται αυτόματα.</p>
            </CardHeader>
            <CardContent className="p-0">
              {bulkLoading ? (
                <div className="p-8 text-center text-gray-500">Φόρτωση...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#004a99] text-white sticky top-0">
                      <tr>
                        <BulkSortHeader field="customer" label="ΠΕΛΑΤΗΣ" />
                        <BulkSortHeader field="serial" label="S/N" />
                        <BulkSortHeader field="model" label="ΜΟΝΤΕΛΟ" />
                        <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A4 BLACK</th>
                        <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A4 COLOR</th>
                        <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A3 BLACK</th>
                        <th className="px-2 py-2 text-center border-l border-white/30" colSpan={2}>A3 COLOR</th>
                      </tr>
                      <tr>
                        <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΠΡΟΗΓ.</th>
                        <th className="px-1 py-1 text-center text-[10px]">ΜΕΤΡΗΤΗΣ</th>
                        <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΠΡΟΗΓ.</th>
                        <th className="px-1 py-1 text-center text-[10px]">ΜΕΤΡΗΤΗΣ</th>
                        <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΠΡΟΗΓ.</th>
                        <th className="px-1 py-1 text-center text-[10px]">ΜΕΤΡΗΤΗΣ</th>
                        <th className="px-1 py-1 text-center text-[10px] border-l border-white/30">ΠΡΟΗΓ.</th>
                        <th className="px-1 py-1 text-center text-[10px]">ΜΕΤΡΗΤΗΣ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sortedBulkMachines.map((m, idx) => {
                        const v = bulkValues[m.machine_id] || {};
                        return (
                          <tr key={m.machine_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-2 py-1 text-xs font-medium max-w-[130px] truncate">{m.customer}</td>
                            <td className="px-2 py-1 text-xs font-mono">{m.serial}</td>
                            <td className="px-2 py-1 text-xs">{m.brand} {m.model}</td>
                            <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{(m.prev_black||0).toLocaleString('el-GR')}</td>
                            <td className="px-1 py-1">
                              <Input type="number" value={v.a4_black} placeholder="0"
                                onChange={e => handleBulkChange(m.machine_id, 'a4_black', e.target.value)}
                                className="h-7 text-xs text-center w-24" />
                            </td>
                            <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{(m.prev_color||0).toLocaleString('el-GR')}</td>
                            <td className="px-1 py-1">
                              <Input type="number" value={v.a4_color} placeholder="0"
                                onChange={e => handleBulkChange(m.machine_id, 'a4_color', e.target.value)}
                                className="h-7 text-xs text-center w-24" />
                            </td>
                            <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{(m.prev_a3||0).toLocaleString('el-GR')}</td>
                            <td className="px-1 py-1">
                              <Input type="number" value={v.a3_black} placeholder="0"
                                onChange={e => handleBulkChange(m.machine_id, 'a3_black', e.target.value)}
                                className="h-7 text-xs text-center w-24" />
                            </td>
                            <td className="px-1 py-1 text-center text-xs text-gray-500 border-l">{(m.prev_a3_color||0).toLocaleString('el-GR')}</td>
                            <td className="px-1 py-1">
                              <Input type="number" value={v.a3_color} placeholder="0"
                                onChange={e => handleBulkChange(m.machine_id, 'a3_color', e.target.value)}
                                className="h-7 text-xs text-center w-24" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="sticky bottom-0 md:bottom-0 py-3 bg-gray-50 border-t z-10">
            <Button onClick={handleBulkSave} className="w-full bg-green-600 hover:bg-green-700 h-12 text-base"
              disabled={bulkSaving || bulkPendingCount === 0} data-testid="save-bulk-btn">
              {bulkSaving ? 'ΑΠΟΘΗΚΕΥΣΗ...' : `ΑΠΟΘΗΚΕΥΣΗ ${bulkPendingCount} ΜΕΤΡΗΤΩΝ (${MONTHS_NAMES[bulkMonth-1]} ${bulkYear})`}
            </Button>
          </div>
        </div>
      ) : (
        /* ========== SINGLE ENTRY VIEW ========== */
        <>

      {/* Quick Search */}
      <Card>
        <CardHeader>
          <CardTitle>ΓΡΗΓΟΡΗ ΑΝΑΖΗΤΗΣΗ ΜΗΧΑΝΗΜΑΤΟΣ</CardTitle>
        </CardHeader>
        <CardContent>
          <MachineSearchBox
            machines={allMachines}
            customers={customers}
            onSelect={(m) => {
              const cust = customers.find(c => c.id === m.customer_id);
              if (cust) {
                setSelectedCustomer(cust.company_name);
                // Set machines for this customer immediately
                const custMachines = allMachines.filter(am => am.customer_id === m.customer_id);
                setMachines(custMachines);
              }
              setSelectedMachine(m.machine_serial);
              setMachineData(m);
              setPreviewReport(null);
            }}
            placeholder="Πληκτρολογήστε S/N, επωνυμία ή ΑΦΜ..."
          />
        </CardContent>
      </Card>

      {/* Customer Selection */}
      <Card>
        <CardHeader>
          <CardTitle>👤 1. ΕΠΙΛΕΞΤΕ ΠΕΛΑΤΗ</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={selectedCustomer}
            onChange={(e) => {
              setSelectedCustomer(e.target.value);
              setSelectedMachine('---');
              setMachineData(null);
              setPreviewReport(null);
            }}
            className="w-full px-4 py-2 border rounded-md"
          >
            <option>---</option>
            {customers.map(c => (
              <option key={c.id} value={c.company_name}>{c.company_name}</option>
            ))}
          </select>
        </CardContent>
      </Card>

      {selectedCustomer !== '---' && (
        <Card>
          <CardHeader>
            <CardTitle>🖥️ 2. ΕΠΙΛΕΞΤΕ ΜΗΧΑΝΗΜΑ</CardTitle>
          </CardHeader>
          <CardContent>
            {machines.length === 0 ? (
              <p className="text-red-600">⚠️ Ο ΠΕΛΑΤΗΣ ΔΕΝ ΕΧΕΙ ΜΗΧΑΝΗΜΑΤΑ.</p>
            ) : (
              <select
                value={selectedMachine}
                onChange={(e) => handleMachineSelect(e.target.value)}
                className="w-full px-4 py-2 border rounded-md"
              >
                <option>---</option>
                {machines.map(m => (
                  <option key={m.id} value={m.machine_serial}>
                    {m.brand} - {m.model} - [SN: {m.machine_serial}]
                  </option>
                ))}
              </select>
            )}
          </CardContent>
        </Card>
      )}

      {machineData && (
        <>
          {/* Year Statistics */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>📊 ΣΤΑΤΙΣΤΙΚΑ ΕΤΟΥΣ</CardTitle>
                <select
                  value={readingYear}
                  onChange={(e) => setReadingYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-3 bg-blue-50 rounded">
                  <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ A4 Black</p>
                  <p className="text-xl font-bold">{(machineData.start_counter_black || 0).toLocaleString()}</p>
                </div>
                {machineData.machine_color === 'Color' && (
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ A4 Color</p>
                    <p className="text-xl font-bold">{(machineData.start_counter_color || 0).toLocaleString()}</p>
                  </div>
                )}
                {isA3 && (
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ A3 Black</p>
                    <p className="text-xl font-bold">{(machineData.start_counter_a3 || 0).toLocaleString()}</p>
                  </div>
                )}
                {isA3Color && (
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <p className="text-sm text-gray-600">ΑΡΧΙΚΟΣ A3 Color</p>
                    <p className="text-xl font-bold">{(machineData.start_counter_a3_color || 0).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {/* 12 Month Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse border">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-2 py-2"></th>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <th key={m} className="border px-2 py-1 text-xs">{MONTHS_GR[m]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-2 py-1 font-semibold bg-gray-50">A4 BLACK</td>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                        const reading = yearReadings.find(r => r.reading_month === m);
                        return (
                          <td key={m} className="border px-2 py-1 text-right">
                            {reading ? reading.counter_value_black?.toLocaleString() : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    {machineData.machine_color === 'Color' && (
                      <tr>
                        <td className="border px-2 py-1 font-semibold bg-gray-50">A4 COLOR</td>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                          const reading = yearReadings.find(r => r.reading_month === m);
                          return (
                            <td key={m} className="border px-2 py-1 text-right">
                              {reading ? reading.counter_value_color?.toLocaleString() : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                    {isA3 && (
                      <tr>
                        <td className="border px-2 py-1 font-semibold bg-gray-50">A3 BLACK</td>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                          const reading = yearReadings.find(r => r.reading_month === m);
                          return (
                            <td key={m} className="border px-2 py-1 text-right">
                              {reading ? (reading.counter_value_a3 || 0).toLocaleString() : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                    {isA3Color && (
                      <tr>
                        <td className="border px-2 py-1 font-semibold bg-gray-50">A3 COLOR</td>
                        {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                          const reading = yearReadings.find(r => r.reading_month === m);
                          return (
                            <td key={m} className="border px-2 py-1 text-right">
                              {reading ? (reading.counter_value_a3_color || 0).toLocaleString() : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* New Reading Form */}
          <Card>
            <CardHeader>
              <CardTitle>📝 ΝΕΑ ΚΑΤΑΧΩΡΗΣΗ</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Year/Month/Mode Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <Label>ΕΤΟΣ</Label>
                  <Input
                    type="number"
                    value={readingYear}
                    onChange={(e) => setReadingYear(parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <Label>ΜΗΝΑΣ</Label>
                  <select
                    value={readingMonth}
                    onChange={(e) => {
                      const month = parseInt(e.target.value);
                      setReadingMonth(month);
                      // Auto-set reading date to last day of selected month
                      const year = readingYear;
                      const lastDay = new Date(year, month, 0).getDate();
                      setReadingDate(`${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`);
                    }}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {Object.entries(MONTHS_GR).map(([num, name]) => (
                      <option key={num} value={num}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>ΗΜΕΡΟΜΗΝΙΑ ΜΕΤΡΗΣΗΣ 📅</Label>
                  <Input
                    type="date"
                    value={readingDate}
                    onChange={(e) => setReadingDate(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Πραγματική ημ/νία</p>
                </div>
              </div>

              <div className="mb-6">
                <div>
                  <Label>ΤΡΟΠΟΣ ΕΙΣΑΓΩΓΗΣ</Label>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => setInputMode('ΜΕΤΡΗΤΗΣ')}
                      className={`flex-1 px-3 py-2 rounded ${inputMode === 'ΜΕΤΡΗΤΗΣ' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                      ΜΕΤΡΗΤΗΣ
                    </button>
                    <button
                      onClick={() => setInputMode('ΣΕΛΙΔΕΣ ΜΗΝΑ')}
                      className={`flex-1 px-3 py-2 rounded ${inputMode === 'ΣΕΛΙΔΕΣ ΜΗΝΑ' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                      ΣΕΛΙΔΕΣ ΜΗΝΑ
                    </button>
                  </div>
                </div>
              </div>

              {/* Previous/Next Info */}
              {existsReading ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                  <p className="font-semibold text-red-800">
                    ❌ ΥΠΑΡΧΕΙ ΗΔΗ ΚΑΤΑΧΩΡΗΣΗ ΓΙΑ {MONTHS_GR[readingMonth]} {readingYear}!
                  </p>
                </div>
              ) : (
                <>
                  {prevReading && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                      <p className="font-semibold">💡 ΠΡΟΗΓΟΥΜΕΝΗ ΕΝΔΕΙΞΗ:</p>
                      <p className="text-sm">
                        A4 Black: <strong>{(prevReading.counter_value_black || 0).toLocaleString()}</strong>
                        {machineData.machine_color === 'Color' && ` | A4 Color: ${(prevReading.counter_value_color || 0).toLocaleString()}`}
                        {isA3 && ` | A3 Black: ${(prevReading.counter_value_a3 || 0).toLocaleString()}`}
                        {isA3Color && ` | A3 Color: ${(prevReading.counter_value_a3_color || 0).toLocaleString()}`}
                      </p>
                    </div>
                  )}

                  {nextReading && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                      <p className="font-semibold">⚡ ΕΠΟΜΕΝΗ ΕΝΔΕΙΞΗ:</p>
                      <p className="text-sm">
                        A4 Black: <strong>{(nextReading.counter_value_black || 0).toLocaleString()}</strong>
                        {machineData.machine_color === 'Color' && ` | A4 Color: ${(nextReading.counter_value_color || 0).toLocaleString()}`}
                        {isA3 && ` | A3 Black: ${(nextReading.counter_value_a3 || 0).toLocaleString()}`}
                        {isA3Color && ` | A3 Color: ${(nextReading.counter_value_a3_color || 0).toLocaleString()}`}
                      </p>
                    </div>
                  )}

                  {/* Source */}
                  <div className="mb-4">
                    <Label>ΠΗΓΗ</Label>
                    <select
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md"
                    >
                      <option>ISS LEX</option>
                      <option>EMAIL</option>
                      <option>PHONE</option>
                      <option>XEPI</option>
                      <option>OTHER</option>
                    </select>
                  </div>

                  {/* Input Fields */}
                  {inputMode === 'ΜΕΤΡΗΤΗΣ' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>ΜΕΤΡΗΤΗΣ A4 BLACK</Label>
                        <Input
                          type="number"
                          min={prevReading?.counter_value_black || machineData.start_counter_black || 0}
                          value={counterBlack}
                          onChange={(e) => setCounterBlack(parseInt(e.target.value))}
                        />
                      </div>
                      {machineData.machine_color === 'Color' && (
                        <div>
                          <Label>ΜΕΤΡΗΤΗΣ A4 COLOR</Label>
                          <Input
                            type="number"
                            min={prevReading?.counter_value_color || machineData.start_counter_color || 0}
                            value={counterColor}
                            onChange={(e) => setCounterColor(parseInt(e.target.value))}
                          />
                        </div>
                      )}
                      {isA3 && (
                        <div>
                          <Label>ΜΕΤΡΗΤΗΣ A3 BLACK</Label>
                          <Input
                            type="number"
                            min={prevReading?.counter_value_a3 || machineData.start_counter_a3 || 0}
                            value={counterA3}
                            onChange={(e) => setCounterA3(parseInt(e.target.value))}
                          />
                        </div>
                      )}
                      {isA3Color && (
                        <div>
                          <Label>ΜΕΤΡΗΤΗΣ A3 COLOR</Label>
                          <Input
                            type="number"
                            min={prevReading?.counter_value_a3_color || machineData.start_counter_a3_color || 0}
                            value={counterA3Color}
                            onChange={(e) => setCounterA3Color(parseInt(e.target.value))}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label>ΣΕΛΙΔΕΣ ΜΗΝΑ A4 BLACK</Label>
                        <Input
                          type="number"
                          min={0}
                          value={pagesBlack}
                          onChange={(e) => setPagesBlack(parseInt(e.target.value))}
                        />
                        <p className="text-sm text-gray-600 mt-1">
                          → ΜΕΤΡΗΤΗΣ: {((prevReading?.counter_value_black || machineData.start_counter_black || 0) + pagesBlack).toLocaleString()}
                        </p>
                      </div>
                      {machineData.machine_color === 'Color' && (
                        <div>
                          <Label>ΣΕΛΙΔΕΣ ΜΗΝΑ A4 COLOR</Label>
                          <Input
                            type="number"
                            min={0}
                            value={pagesColor}
                            onChange={(e) => setPagesColor(parseInt(e.target.value))}
                          />
                          <p className="text-sm text-gray-600 mt-1">
                            → ΜΕΤΡΗΤΗΣ: {((prevReading?.counter_value_color || machineData.start_counter_color || 0) + pagesColor).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {isA3 && (
                        <div>
                          <Label>ΣΕΛΙΔΕΣ ΜΗΝΑ A3 BLACK</Label>
                          <Input
                            type="number"
                            min={0}
                            value={pagesA3}
                            onChange={(e) => setPagesA3(parseInt(e.target.value))}
                          />
                          <p className="text-sm text-gray-600 mt-1">
                            → ΜΕΤΡΗΤΗΣ: {((prevReading?.counter_value_a3 || machineData.start_counter_a3 || 0) + pagesA3).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {isA3Color && (
                        <div>
                          <Label>ΣΕΛΙΔΕΣ ΜΗΝΑ A3 COLOR</Label>
                          <Input
                            type="number"
                            min={0}
                            value={pagesA3Color}
                            onChange={(e) => setPagesA3Color(parseInt(e.target.value))}
                          />
                          <p className="text-sm text-gray-600 mt-1">
                            → ΜΕΤΡΗΤΗΣ: {((prevReading?.counter_value_a3_color || machineData.start_counter_a3_color || 0) + pagesA3Color).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <Button onClick={handleCheckAndReport} className="w-full">
                    🔍 ΕΛΕΓΧΟΣ & ΔΗΜΙΟΥΡΓΙΑ Report
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Preview Report */}
          {previewReport && (
            <Card>
              <CardHeader>
                <CardTitle>📋 ΠΡΟΕΠΙΣΚΟΠΗΣΗ</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-100 p-4 rounded text-sm whitespace-pre-wrap font-mono">
                  {previewReport.report}
                </pre>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Button
                    onClick={handleFinalSave}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    ✅ ΟΡΙΣΤΙΚΗ ΚΑΤΑΧΩΡΗΣΗ
                  </Button>
                  <Button
                    onClick={handleCancelPreview}
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                  >
                    ❌ ΑΚΥΡΩΣΗ
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
};

export default ReadingsPageNew;
